import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Check, Upload } from 'lucide-react';
import { Button, type ButtonProps } from '@shared/ui/primitives/Button';
import { Dropdown } from '@shared/ui/primitives/Dropdown';
import { cn } from '@shared/lib/cn';
import { useTabBarHidden } from '@shared/hooks/useTabBarHidden';

type Tone = 'default' | 'danger';
type SelectionControlType = 'checkbox' | 'radio';
const TAB_BAR_OFFSET = 'calc(4.25rem + env(safe-area-inset-bottom, 0px))';
const DEFAULT_FIXED_ACTIONS_HEIGHT = 92;

function describedBy(id: string, helper?: ReactNode, error?: ReactNode): string | undefined {
  if (error) return `${id}-error`;
  if (helper) return `${id}-helper`;
  return undefined;
}

/** Authored guidance under a field's label, set as the OTP action's help line already is. */
export function FieldHint({ children }: { children: ReactNode }): JSX.Element {
  return <p className="text-body-sm text-ink-secondary">{children}</p>;
}

function FieldShell({
  label,
  id,
  hint,
  helper,
  error,
  required,
  optional,
  children,
  className,
}: {
  label: ReactNode;
  id: string;
  /** Guidance the author wrote, read before the control. `helper` is the caption below it. */
  hint?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  optional?: boolean;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="flex items-baseline justify-between gap-3 text-body-sm font-semibold text-ink-secondary">
        <span>
          {label}
          {required ? <span className="ml-0.5 text-danger">*</span> : null}
        </span>
        {optional ? <span className="text-body-sm font-normal text-ink-muted">Optional</span> : null}
      </label>
      {hint ? <FieldHint>{hint}</FieldHint> : null}
      {children}
      {helper && !error ? (
        <p id={`${id}-helper`} className="text-caption text-ink-muted">
          {helper}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-caption text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const fieldBaseClass = cn(
  'w-full rounded-md border border-hairline bg-elevated text-body text-ink',
  'placeholder:text-ink-muted focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted',
);

function fieldToneClass(tone: Tone): string {
  return tone === 'danger' ? 'border-danger focus:border-danger focus:ring-danger/10' : '';
}

export function FormStack({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn('flex flex-col gap-4 px-5 pt-7 pb-safe-or-6', className)}>{children}</div>;
}

export function FormSection({
  title,
  description,
  children,
  className,
  contentClassName,
  titleClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  titleClassName?: string;
}): JSX.Element {
  return (
    <section className={cn('rounded-lg bg-card hairline p-4', className)}>
      {title || description ? (
        <div className="mb-4">
          {title ? (
            <h2
              className={cn(
                'text-body font-semibold text-ink',
                titleClassName,
              )}
            >
              {title}
            </h2>
          ) : null}
          {description ? <p className="mt-1 text-body-sm text-ink-muted">{description}</p> : null}
        </div>
      ) : null}
      <div className={cn('flex flex-col gap-4', contentClassName)}>{children}</div>
    </section>
  );
}

export function FieldGroup({
  label,
  description,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  /** Guidance the author wrote, read before the options. `description` is this app's own sub-label. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <fieldset className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-0.5">
        <div className="text-body-sm font-semibold text-ink-secondary">{label}</div>
        {description ? <p className="text-body-sm text-ink-muted">{description}</p> : null}
        {hint ? <FieldHint>{hint}</FieldHint> : null}
      </div>
      {children}
    </fieldset>
  );
}

/**
 * The write-in box a choice list reveals once "Other" is picked. Deliberately unlabelled: the option
 * directly above it is its label, and what the resident types is stored as the answer itself.
 */
export function OtherInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <input
      id={id}
      type="text"
      aria-label={label}
      placeholder={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(fieldBaseClass, 'h-12 px-3.5')}
    />
  );
}

interface TextInputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  hint?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  inputClassName?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
}

export function TextInputField({
  label,
  hint,
  helper,
  error,
  optional,
  required,
  id,
  className,
  inputClassName,
  startAdornment,
  endAdornment,
  ...inputProps
}: TextInputFieldProps): JSX.Element {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const tone: Tone = error ? 'danger' : 'default';

  return (
    <FieldShell
      id={inputId}
      label={label}
      hint={hint}
      helper={helper}
      error={error}
      required={required}
      optional={optional}
      className={className}
    >
      <div className="relative">
        {startAdornment ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body-sm text-ink-muted">
            {startAdornment}
          </span>
        ) : null}
        <input
          id={inputId}
          aria-describedby={describedBy(inputId, helper, error)}
          aria-invalid={error ? true : undefined}
          required={required}
          className={cn(
            fieldBaseClass,
            'h-12 px-3.5',
            startAdornment && 'pl-9',
            endAdornment && 'pr-10',
            fieldToneClass(tone),
            inputClassName,
          )}
          {...inputProps}
        />
        {endAdornment ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted">
            {endAdornment}
          </span>
        ) : null}
      </div>
    </FieldShell>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  hint?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  textareaClassName?: string;
}

export function TextAreaField({
  label,
  hint,
  helper,
  error,
  optional,
  required,
  id,
  className,
  textareaClassName,
  rows = 4,
  ...textareaProps
}: TextAreaFieldProps): JSX.Element {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  return (
    <FieldShell
      id={textareaId}
      label={label}
      hint={hint}
      helper={helper}
      error={error}
      required={required}
      optional={optional}
      className={className}
    >
      <textarea
        id={textareaId}
        rows={rows}
        aria-describedby={describedBy(textareaId, helper, error)}
        aria-invalid={error ? true : undefined}
        required={required}
        className={cn(
          fieldBaseClass,
          'min-h-28 resize-y px-3.5 py-3',
          fieldToneClass(error ? 'danger' : 'default'),
          textareaClassName,
        )}
        {...textareaProps}
      />
    </FieldShell>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  hint?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  selectClassName?: string;
  children: ReactNode;
}

export function SelectField({
  label,
  hint,
  helper,
  error,
  optional,
  required,
  id,
  className,
  selectClassName,
  children,
  ...selectProps
}: SelectFieldProps): JSX.Element {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <FieldShell
      id={selectId}
      label={label}
      hint={hint}
      helper={helper}
      error={error}
      required={required}
      optional={optional}
      className={className}
    >
      <Dropdown
        id={selectId}
        aria-describedby={describedBy(selectId, helper, error)}
        aria-invalid={error ? true : undefined}
        required={required}
        tone={error ? 'danger' : 'default'}
        className={selectClassName}
        {...selectProps}
      >
        {children}
      </Dropdown>
    </FieldShell>
  );
}

export function CheckboxOption({
  label,
  description,
  checked,
  disabled,
  onChange,
  name,
  value,
  prose,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  name?: string;
  value?: string;
  /** Set the label in regular weight rather than the default option label's
   *  semibold — for sentence-length acceptance copy. Same size as other options. */
  prose?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <label
      className={cn(
        'flex min-h-14 items-start gap-3 rounded-md border px-3.5 py-3 transition-colors',
        disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
        checked && !disabled
          ? 'border-primary-600 bg-primary-50'
          : 'border-hairline bg-elevated',
        className,
      )}
    >
      <input
        type="checkbox"
        name={name}
        value={value}
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <SelectionControl type="checkbox" checked={checked} disabled={disabled} />
      <OptionText label={label} description={description} prose={prose} />
    </label>
  );
}

export function RadioOption<T extends string>({
  label,
  description,
  value,
  selected,
  disabled,
  onChange,
  name,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  value: T;
  selected: boolean;
  disabled?: boolean;
  onChange: (value: T) => void;
  name?: string;
  className?: string;
}): JSX.Element {
  return (
    <label
      className={cn(
        'flex min-h-14 items-start gap-3 rounded-md border px-3.5 py-3 transition-colors',
        disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
        selected && !disabled
          ? 'border-primary-600 bg-primary-50'
          : 'border-hairline bg-elevated',
        className,
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        className="peer sr-only"
        checked={selected}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <SelectionControl type="radio" checked={selected} disabled={disabled} />
      <OptionText label={label} description={description} />
    </label>
  );
}

export function SelectableTile({
  title,
  description,
  detail,
  badges,
  selected,
  disabled,
  onSelect,
  trailing,
  className,
  selectionType = 'checkbox',
}: {
  title: ReactNode;
  description?: ReactNode;
  detail?: ReactNode;
  badges?: ReactNode;
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  trailing?: ReactNode;
  className?: string;
  selectionType?: SelectionControlType;
}): JSX.Element {
  const sharedClassName = cn(
    'group flex items-start gap-3 rounded-lg border px-4 py-3 text-left',
    disabled ? 'cursor-not-allowed opacity-55' : onSelect ? 'cursor-pointer' : '',
    selected
      ? 'border-primary-300 bg-primary-50'
      : 'border-hairline bg-card md:hover:bg-sunken',
    className,
  );

  const content = (
    <>
      <SelectionControl type={selectionType} checked={selected} disabled={disabled} />

      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0 flex-1">
            <span className="block [overflow-wrap:anywhere] text-body-sm font-semibold text-ink">
              {title}
            </span>
            {description ? (
              <span className="mt-0.5 block [overflow-wrap:anywhere] text-caption text-ink-secondary">
                {description}
              </span>
            ) : null}
            {detail ? (
              <span className="mt-0.5 block text-caption text-ink-secondary">{detail}</span>
            ) : null}
          </span>

          {trailing ? <span className="shrink-0">{trailing}</span> : null}
        </span>

        {badges ? <span className="mt-2 flex flex-wrap gap-2">{badges}</span> : null}
      </span>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onSelect}
        className={sharedClassName}
        aria-pressed={selected}
      >
        {content}
      </button>
    );
  }

  return <div className={sharedClassName}>{content}</div>;
}

function SelectionControl({
  type,
  checked,
  disabled,
}: {
  type: SelectionControlType;
  checked: boolean;
  disabled?: boolean;
}): JSX.Element {
  const isCheckbox = type === 'checkbox';

  return (
    <span
      className={cn(
        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors',
        'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-600/20 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-paper',
        'group-focus-visible:ring-2 group-focus-visible:ring-primary-600/20 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-paper',
        isCheckbox ? 'rounded-[4px]' : 'rounded-pill',
        disabled && 'opacity-60',
        isCheckbox
          ? checked
            ? 'border-primary-700 bg-primary-700 text-white'
            : 'border-hairline bg-elevated text-transparent'
          : checked
            ? 'border-primary-700 bg-elevated text-primary-700'
            : 'border-hairline bg-elevated text-transparent',
      )}
      aria-hidden
    >
      {isCheckbox ? (
        <Check className="h-3.5 w-3.5" strokeWidth={2.25} />
      ) : (
        <span className={cn('h-2.5 w-2.5 rounded-pill bg-current', checked ? 'opacity-100' : 'opacity-0')} />
      )}
    </span>
  );
}

function OptionText({
  label,
  description,
  prose,
}: {
  label: ReactNode;
  description?: ReactNode;
  prose?: boolean;
}): JSX.Element {
  return (
    <span className="min-w-0">
      <span className={cn('block text-body-sm text-ink', !prose && 'font-semibold')}>
        {label}
      </span>
      {description ? <span className="mt-0.5 block text-caption text-ink-muted">{description}</span> : null}
    </span>
  );
}

export function FileDropField({
  label,
  helper,
  error,
  accept,
  multiple,
  disabled,
  onFiles,
  className,
}: {
  label: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: FileList) => void;
  className?: string;
}): JSX.Element {
  const generatedId = useId();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) onFiles(event.target.files);
  };

  return (
    <FieldShell id={generatedId} label={label} helper={helper} error={error} className={className}>
      <label
        htmlFor={generatedId}
        className={cn(
          'flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-hairline bg-elevated px-4 py-5 text-center transition-colors',
          'hover:bg-sunken',
          disabled && 'pointer-events-none opacity-55',
          error && 'border-danger',
        )}
      >
        <Upload className="h-5 w-5 text-ink-muted" strokeWidth={1.75} />
        <span className="mt-2 text-body-sm font-semibold text-ink">Choose file</span>
        <span className="mt-0.5 text-caption text-ink-muted">
          {multiple ? 'Select one or more files' : 'Select a file'}
        </span>
      </label>
      <input
        id={generatedId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="sr-only"
        aria-describedby={describedBy(generatedId, helper, error)}
        aria-invalid={error ? true : undefined}
        onChange={handleChange}
      />
    </FieldShell>
  );
}

export function FormActions({
  children,
  sticky = false,
  className,
}: {
  children: ReactNode;
  sticky?: boolean;
  className?: string;
}): JSX.Element {
  if (sticky) {
    return <FixedBottomFormActions className={className}>{children}</FixedBottomFormActions>;
  }

  return (
    <div
      className={cn('flex flex-col gap-3', className)}
    >
      {children}
    </div>
  );
}

export function FixedBottomFormActions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const tabBarHidden = useTabBarHidden();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(DEFAULT_FIXED_ACTIONS_HEIGHT);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      setFooterHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(node);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <>
      <div
        aria-hidden="true"
        className="shrink-0"
        style={{ height: `${footerHeight}px` }}
      />
      <div
        className="frosted-surface fixed inset-x-0 z-40 border-t border-hairline shadow-sheet"
        style={{ bottom: tabBarHidden ? '0px' : TAB_BAR_OFFSET }}
      >
        <div
          ref={containerRef}
          className={cn(
            'flex flex-col gap-3 px-5 pt-3',
            tabBarHidden ? 'pb-safe-or-3' : 'pb-2',
            className,
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}

export function SubmitButton(props: ButtonProps): JSX.Element {
  return <ActionButton {...props} />;
}

export function ActionButton({ className, variant = 'primary', ...props }: ButtonProps): JSX.Element {
  return (
    <Button
      {...props}
      variant={variant}
      size="md"
      width="full"
      className={cn('text-body', className)}
    />
  );
}
